import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import * as z from 'zod';

import { DashboardLayout } from '#modules/user/features/dashboard/components/DashboardLayout';
import { Friends } from '#modules/user/features/friends/components/Friends';
import { InviteSessionPicker } from '#modules/user/features/friends/components/Friends/InviteSessionPicker';
import type { InvitableSession } from '#modules/user/features/friends/hooks/useInvitableSessions';
import type { PeerSession } from '#modules/user/features/friends/model/peerSessions';
import type { ShelfPerson } from '#modules/user/features/friends/model/peopleShelves';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Button } from '#features/ui';

const ME = '44444444-4444-4444-8444-444444444444';
const HOST = '55555555-5555-4555-8555-555555555555';
const GAME = '77777777-7777-4777-8777-777777777777';
const schema = z.object({
  lang: z.enum(['ru', 'en']).default('ru'),
  view: z.enum(['friends', 'invite']).default('friends'),
  timeFormat: z.enum(['TWELVE', 'TWENTY_FOUR']).default('TWENTY_FOUR'),
  tab: z.enum(['friends', 'following', 'followers', 'recommended']).default('friends'),
  role: z.enum(['guest', 'member', 'invited', 'host']).default('guest'),
  state: z
    .enum([
      'normal',
      'playing',
      'alignment',
      'full',
      'large',
      'empty',
      'loading',
      'error',
      'pending',
    ])
    .default('normal'),
});

export const Route = createFileRoute('/{-$locale}/_layout/preview/friends')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: zodValidator(schema),
  component: FriendsPreviewRoute,
});

function FriendsPreviewRoute() {
  const search = Route.useSearch();
  return <FriendsPreview key={JSON.stringify(search)} {...search} />;
}

function FriendsPreview({ lang, role, state, view, timeFormat }: z.infer<typeof schema>) {
  const [open, setOpen] = useState(true);
  const [i18n] = useState(() =>
    setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } })
  );
  const [client] = useState(() => {
    const value = new QueryClient();
    const queries = value.defaultQueryOptions.bind(value);
    const mutations = value.defaultMutationOptions.bind(value);
    // Витрина не читает API и не меняет данные текущего пользователя.
    value.defaultQueryOptions = (options) => ({
      ...queries(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
      queryFn: async () => {
        throw new Error('Isolated preview cannot access the API');
      },
    });
    value.defaultMutationOptions = (options) => ({
      ...mutations(options),
      mutationFn: async () => {
        // Воспроизводимое ожидание без обращения к серверу, снимается перезагрузкой превью.
        if (view === 'invite' && state === 'pending') await new Promise(() => {});
        return { __typename: 'Operation', status: 'SUCCESS' };
      },
      retry: false,
    });
    value.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: ME,
      gameIds: [GAME],
      timeFormat,
      timeZoneOffset: 3,
      oauth: { discord: { id: 'preview-discord' } },
    });
    value.setQueryData(
      ['games'],
      [{ __typename: 'Game', id: GAME, name: 'Apex Legends', providers: [] }]
    );
    value.setQueryData(['platforms'], []);
    value.setQueryData(['my-active-sessions'], { records: [] });
    value.setQueryData(['follow-counts'], {
      friends: 1,
      following: 2,
      followers: 2,
      newFollowers: 1,
      unseenFollowers: 0,
    });
    const edge = {
      userId: HOST,
      createdAt: new Date().toISOString(),
      mutual: true,
      user: { userId: HOST, displayName: 'juggers' },
    };
    for (const kind of ['friends', 'following', 'followers', 'followingOnly', 'newFollowers']) {
      value.setQueryData(['follow-list', kind, null], {
        pages: [{ items: [edge], nextCursor: null }],
        pageParams: [null],
      });
    }
    const people: ShelfPerson[] = Array.from({ length: 10 }, (_, index) => ({
      userId: `preview-person-${index}`,
      sessionsTogether: state === 'alignment' ? (index < 4 ? 1 : 0) : index === 1 ? 1 : 0,
      confirmedPlayed: false,
      lastPlayedAt:
        state === 'alignment' && index < 4
          ? new Date(Date.now() - [120000, 172800000, 604800000, 2332800000][index]).toISOString()
          : undefined,
      talked: state === 'alignment' ? index === 4 : index === 0,
      called: false,
      commented: false,
      looksForTeam: false,
      followedByViewer: false,
      followState: 'NONE',
      gameIds: [GAME],
      user: {
        userId: `preview-person-${index}`,
        displayName:
          state === 'alignment'
            ? ([
                'scB971017',
                'qABB15150',
                'Long_Player_Name_For_Wrapping',
                'ALEXdddddddddddddddd',
                'Duo202032',
              ][index] ?? `Player ${index + 1}`)
            : index === 0
              ? 'Очень_длинное_имя_игрока_для_проверки'
              : `Player ${index + 1}`,
      },
    }));
    const ids = [HOST, ...people.map((p) => p.userId)].toSorted();
    const presence = ids.map((userId) => ({
      userId,
      status: userId === HOST ? 'ONLINE' : 'OFFLINE',
      playingNow: userId === HOST || state === 'playing',
      gameId: userId === HOST ? GAME : null,
      gameName:
        userId === HOST
          ? 'Apex Legends'
          : state === 'playing'
            ? 'Counter-Strike 2: Premier Competitive Edition'
            : null,
      lastSeenAt: new Date(
        Date.now() -
          (state === 'alignment' && userId === 'preview-person-0' ? 180000 : 3600000 * 11)
      ).toISOString(),
    }));
    for (const queryIds of [ids, [HOST], people.map((p) => p.userId).toSorted()]) {
      value.setQueryData(['playerAvailability', queryIds, null], presence);
    }
    value.setQueryData(['lookingForTeam'], new Map());
    if (state !== 'loading' && state !== 'error') {
      value.setQueryData(
        ['acquaintances'],
        state === 'empty' ? [] : people.slice(0, state === 'alignment' ? 5 : 2)
      );
      value.setQueryData(['playersByGame'], {
        pages: [
          {
            items: state === 'empty' ? [] : people.slice(state === 'alignment' ? 5 : 2),
            nextCursor: null,
          },
        ],
        pageParams: [null],
      });
    }
    if (state === 'error') {
      for (const queryKey of [['acquaintances'], ['playersByGame']]) {
        value
          .getQueryCache()
          .build(value, { queryKey })
          .setState({
            status: 'error',
            error: new Error('Preview load failure'),
            fetchStatus: 'idle',
          });
      }
    }
    const sessions: PeerSession[] = ['DISCORD', 'PLATFORM', 'IN_GAME', 'NONE'].map(
      (voiceMode, index) => {
        const total = state === 'large' ? (view === 'invite' ? 7 : 10) : 3;
        const count = state === 'full' ? total : state === 'large' ? total - 1 : 2;
        const hostId = role === 'host' ? ME : HOST;
        const startedAt = new Date(Date.now() + (index === 1 ? -1800000 : 86400000)).toISOString();
        const expiredAt = new Date(new Date(startedAt).getTime() + 7200000).toISOString();
        const members = Array.from({ length: count }, (_, member) => ({
          memberId:
            member === 0
              ? hostId
              : member === 1 && role === 'member'
                ? ME
                : `preview-member-${member}`,
          status: 'ACCEPTED',
          userData: {
            displayName: member === 0 ? 'juggers' : `Player ${member}`,
            avatar: null,
            socials: [],
          },
        }));
        if (role === 'invited')
          members.push({
            memberId: ME,
            status: 'INVITED',
            userData: { displayName: 'Me', avatar: null, socials: [] },
          });
        return {
          sessionId: `preview-session-${index}`,
          postId: `preview-post-${index}`,
          userId: hostId,
          gameId: GAME,
          status: index === 1 ? 'LIVE' : 'UPCOMING',
          host: { displayName: 'juggers' },
          session: {
            id: `preview-session-${index}`,
            startedAt,
            expiredAt,
            status: index === 1 ? 'LIVE' : 'UPCOMING',
            teamLimit: total,
            acceptedMembersCount: count,
            discordVoice: voiceMode === 'DISCORD',
            voiceMode,
            voiceRoom: { connectedCount: 1, status: 'OPEN' },
            members,
          },
        };
      }
    );
    value.setQueryData(['peer-sessions'], state === 'empty' ? [] : sessions);
    return value;
  });
  const invitableSessions: InvitableSession[] = (
    client.getQueryData<PeerSession[]>(['peer-sessions']) ?? []
  ).flatMap(({ session, gameId, sessionId }, index) =>
    session
      ? [
          {
            id: sessionId,
            gameId,
            status: session.status ?? 'UPCOMING',
            startedAt:
              index === 0
                ? new Date(Date.now() + 3600000).toISOString()
                : index === 3
                  ? new Date(Date.now() + 3 * 86400000).toISOString()
                  : session.startedAt,
            taken: session.acceptedMembersCount ?? 0,
            total: session.teamLimit ?? 0,
            voice: session.voiceMode !== 'NONE',
            voiceMode: session.voiceMode,
            memberIds: new Set(role === 'member' && index === 2 ? ['preview-invitee'] : []),
            accepted: (session.members ?? []).flatMap((member) =>
              member?.memberId && member.status === 'ACCEPTED'
                ? [
                    {
                      userId: member.memberId,
                      avatar: member.userData?.avatar,
                      displayName: member.userData?.displayName,
                    },
                  ]
                : []
            ),
          },
        ]
      : []
  );
  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <DashboardLayout isAuth>
          {view === 'invite' ? (
            // Фон окна не должен вести на вымышленные заявки из соседней витрины.
            <Button alignSelf="flex-start" onClick={() => setOpen(true)}>
              <Trans>Invite</Trans>
            </Button>
          ) : (
            <Friends />
          )}
          {view === 'invite' ? (
            <InviteSessionPicker
              open={open}
              onOpenChange={setOpen}
              sessions={invitableSessions}
              userId="preview-invitee"
              displayName="Alex_with_a_very_long_display_name"
            />
          ) : null}
        </DashboardLayout>
      </QueryClientProvider>
    </I18nProvider>
  );
}
